import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Building2, Plus, Trash2, LogOut, Users, BedDouble, Calendar, IndianRupee, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function MasterPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  async function load() {
    try {
      const [{ data: s }, { data: p }] = await Promise.all([
        api.get("/master/stats"),
        api.get("/master/properties"),
      ]);
      setStats(s); setProperties(p.properties);
    } catch (e) {
      if (e.response?.status === 403) { toast.error("Master admin only"); navigate("/dashboard"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function handleDelete() {
    if (!deleting) return;
    try {
      await api.delete(`/master/properties/${deleting.id}`);
      toast.success(`${deleting.name} removed`);
      setDeleting(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  }

  if (loading || !stats) return <div className="min-h-screen flex items-center justify-center bg-[#0B101E] text-[#D4AF37]">Loading master panel…</div>;

  return (
    <div className="min-h-screen bg-[#0B101E] text-white" data-testid="master-panel">
      <header className="border-b border-white/5 bg-[#0a0f1c]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-[#D4AF37] flex items-center justify-center text-[#0B101E] font-bold font-display">B</span>
            <div>
              <div className="font-display text-lg">Booking4U <span className="text-[#D4AF37] text-xs uppercase tracking-widest ml-2">Master</span></div>
              <div className="text-xs text-white/40">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAdding(true)} className="rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="master-add-property-btn">
              <Plus className="w-3.5 h-3.5" /> Add property
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 text-white/60 hover:text-white" data-testid="master-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">System overview</div>
          <h1 className="font-display text-3xl md:text-4xl">All properties</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
          <Stat label="Properties" value={stats.properties} icon={Building2} />
          <Stat label="Users" value={stats.users} icon={Users} />
          <Stat label="Rooms" value={stats.rooms} icon={BedDouble} />
          <Stat label="Reservations" value={stats.reservations} icon={Calendar} />
          <Stat label="Revenue total" value={`₹${Number(stats.revenue_total).toLocaleString("en-IN")}`} icon={IndianRupee} />
        </div>

        <div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {properties.map((p) => (
              <div key={p.id} className="bg-[#12182B] border border-white/5 rounded-xl p-6 flex flex-col" data-testid={`property-card-${p.id}`}>
                {p.hero?.data_url ? (
                  <img src={p.hero.data_url} alt={p.name} className="w-full h-32 object-cover rounded-md mb-4 border border-white/5" />
                ) : (
                  <div className="w-full h-32 rounded-md mb-4 bg-gradient-to-br from-[#1A2235] to-[#0a0f1c] border border-white/5 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-white/20" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-xl">{p.name}</div>
                    <div className="text-xs text-white/50 mt-1">{p.location}</div>
                  </div>
                  <button onClick={() => setDeleting(p)} title="Remove" className="text-white/30 hover:text-red-300" data-testid={`delete-prop-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                  <Mini label="Rooms" value={p.rooms_count} />
                  <Mini label="Bookings" value={p.reservations_count} />
                  <Mini label="Revenue" value={`₹${(p.revenue_total / 1000).toFixed(0)}k`} />
                </div>
                {p.admin && (
                  <div className="mt-4 pt-4 border-t border-white/5 text-xs text-white/55 flex items-center gap-2">
                    <Mail className="w-3 h-3 text-[#D4AF37]" /> {p.admin.email}
                  </div>
                )}
                <a href={`/book/${p.id}`} target="_blank" rel="noreferrer" className="mt-4 text-xs text-[#D4AF37] hover:text-[#F2D06B] inline-flex items-center gap-1">
                  Booking page <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
            {properties.length === 0 && (
              <div className="col-span-full text-center text-white/40 py-12">
                No properties yet. <button onClick={() => setAdding(true)} className="text-[#D4AF37] underline">Add the first one</button>.
              </div>
            )}
          </div>
        </div>
      </main>

      <AddPropertyDialog open={adding} onClose={() => setAdding(false)} onCreated={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the property and all its data (rooms, reservations, reviews, channels, admin user). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-prop-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500/80 hover:bg-red-500" data-testid="delete-prop-confirm">
              Yes, remove permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-[#12182B] border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37]/70" />}
      </div>
      <div className="font-display text-2xl mt-3">{value}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-[#0a0f1c] rounded-md px-2.5 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-white/90 mt-0.5">{value}</div>
    </div>
  );
}

function AddPropertyDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    hotel_name: "", location: "India",
    admin_name: "", admin_email: "", admin_password: "",
  });
  const [saving, setSaving] = useState(false);
  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/master/properties", form);
      toast.success(`Property "${data.name}" created with admin ${data.admin.email}`);
      setForm({ hotel_name: "", location: "India", admin_name: "", admin_email: "", admin_password: "" });
      onCreated(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-[#0F1424] border-white/10 text-white" data-testid="add-property-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add new property</DialogTitle>
          <DialogDescription className="text-white/50">
            Provision a property + its admin login. The admin will sign in at <code className="text-[#D4AF37]">/login</code> and see only this property.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="Property name *">
              <Input value={form.hotel_name} onChange={(e) => upd("hotel_name", e.target.value)} required data-testid="prop-hotel-name" className="bg-[#0a0f1c] border-white/10" />
            </Fld>
            <Fld label="Location">
              <Input value={form.location} onChange={(e) => upd("location", e.target.value)} data-testid="prop-location" className="bg-[#0a0f1c] border-white/10" />
            </Fld>
          </div>
          <div className="text-xs uppercase tracking-widest text-white/50 pt-2 border-t border-white/5">Admin login for this property</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Fld label="Admin name *">
              <Input value={form.admin_name} onChange={(e) => upd("admin_name", e.target.value)} required data-testid="prop-admin-name" className="bg-[#0a0f1c] border-white/10" />
            </Fld>
            <Fld label="Admin email *">
              <Input type="email" value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)} required data-testid="prop-admin-email" className="bg-[#0a0f1c] border-white/10" />
            </Fld>
            <Fld label="Admin password *" className="md:col-span-2">
              <Input type="password" minLength={6} value={form.admin_password} onChange={(e) => upd("admin_password", e.target.value)} required data-testid="prop-admin-password" className="bg-[#0a0f1c] border-white/10" />
              <div className="text-[11px] text-white/40 mt-1">Min 6 chars. Share securely with the property admin.</div>
            </Fld>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm border border-white/10 text-white/70 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={saving} className="btn-gold rounded-md px-4 py-2 text-sm disabled:opacity-60" data-testid="prop-submit">
              {saving ? "Creating…" : "Create property"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-widest text-white/50 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
